import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SAP_COLORS, SAP_TYPOGRAPHY } from '../../theme/sapDesignSystem';

export interface BackToLinkProps {
  /** Label next to the back arrow (e.g. "Back to flow", "Back to list") */
  label: string;
  /** Called when the user clicks the back control */
  onClick: () => void;
  /** Optional aria-label for the icon button */
  ariaLabel?: string;
}

/**
 * Consistent "back" control for detail views (e.g. Step Detail → Flow, or any drill-down).
 * Use for returning to parent context without losing list/flow state.
 */
const BackToLink: React.FC<BackToLinkProps> = ({
  label,
  onClick,
  ariaLabel,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        color: SAP_COLORS.sapGray7,
        '&:hover': { color: SAP_COLORS.sapBlue },
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={ariaLabel ?? label}
    >
      <IconButton
        size="small"
        aria-hidden
        sx={{
          color: 'inherit',
          '&:hover': { backgroundColor: SAP_COLORS.sapGray2 },
        }}
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold, fontSize: '0.9375rem' }}>
        {label}
      </Typography>
    </Box>
  );
};

export default BackToLink;
